/**
 * The acquisition root "Lumio Channels" cards, sourced from real data for the LIVE
 * channels (only Web today; the rest are frosted "Unlocking soon" and keep their hidden
 * mock figures). Each live card shows the same numbers as its channel-detail page:
 *
 *  - **Spent** = the channel's ad-spend today (from {@link useAcquisitionChannelFigures}).
 *  - **Budget** = the channel's daily limit from `acquisition-budget` `budget_details[aft]`
 *    (read from the slice the page already fetches), driving the bar + pacing state.
 *  - **CPA** = the blended-CPA label from {@link useAcquisitionChannelFigures}.
 *
 * The spend + FTD fetch and CPA derivation now live in {@link useAcquisitionChannelFigures},
 * shared with the "Ad Budget & Daily Limits" modal so card and modal never disagree.
 * Today re-polls ~every 5 min in place; reads live, so the test tenant shows $0 spend /
 * `–` CPA until a campaign delivers. Contract: `docs/api.owners-hub.acquisition-campaigns.md`.
 */
import React from 'react';

import { useAppSelector } from '@/store';
import { selectAcquisitionBudget } from '@/store/slices/acquisitionBudget';

import type { ChannelState } from '../../theme/tokens';
import type { LumioChannel } from '../../mock-data/types';
import { acquisitionAftFor, isAcquisitionChannelEnabled } from '../../config/acquisitionChannels';
import { finiteNumber } from '../../utils/money';
import { useAcquisitionChannelFigures } from './useAcquisitionChannelFigures';

export type AcquisitionChannelsResult = { channels: LumioChannel[]; loading: boolean };

/** Pacing state from spend vs the daily limit (mirrors the channel-detail budget card). */
const pacingState = (spent: number, limit: number): ChannelState => {
  if (limit <= 0) return 'active';
  const pct = (spent / limit) * 100;
  return pct >= 100 ? 'exhausted' : pct >= 90 ? 'nearing-budget' : 'active';
};

/**
 * @param tenantId Active brand tenant id (`displayBrand?.tenant_id`) for the Ad Service.
 * @param casinoInstanceId Active brand id (`displayBrand?.id`) for the Stats + budget.
 * @param mockChannels The channel-card scaffold (names / icons / order); the live channels'
 *        figures are overridden, the locked ones keep their (hidden) mock figures.
 */
export const useAcquisitionChannels = (
  tenantId: string | undefined,
  casinoInstanceId: string | undefined,
  mockChannels: LumioChannel[],
): AcquisitionChannelsResult => {
  const { figures, loading: figuresLoading } = useAcquisitionChannelFigures(tenantId, casinoInstanceId);

  /* Budget: the per-channel daily limits from the acquisition budget the page fetches. */
  const acqBudget = useAppSelector((s) => selectAcquisitionBudget(s, casinoInstanceId));
  const budgetGlobalLoading = useAppSelector((s) => s.acquisitionBudget.loading);
  const budgetLoading = Boolean(casinoInstanceId) && acqBudget === undefined && budgetGlobalLoading;

  const channels = React.useMemo<LumioChannel[]>(
    () =>
      mockChannels.map((channel) => {
        const aft = acquisitionAftFor(channel.id);
        // Locked / non-acquisition cards keep their (frosted, hidden) mock figures.
        if (!aft || !isAcquisitionChannelEnabled(channel.id)) return channel;
        const fig = figures[aft];
        const details = acqBudget?.budget_details as Record<string, unknown> | null | undefined;
        const limit = finiteNumber(details?.[aft]);
        const spent = fig?.spent ?? 0;
        const hasLimit = limit > 0;
        return {
          ...channel,
          spent,
          budget: hasLimit ? Math.round(limit) : 0,
          progress: hasLimit ? Math.min(100, Math.max(0, Math.round((spent / limit) * 100))) : 0,
          cpa: fig?.cpaLabel ?? channel.cpa,
          state: pacingState(spent, limit),
        };
      }),
    [mockChannels, figures, acqBudget],
  );

  return { channels, loading: figuresLoading || budgetLoading };
};

export default useAcquisitionChannels;
